/**
 * components/procurement/BudgetBuildProgress.jsx — the "preparing your workspace"
 * journey shown while a new budget's baseline is being built asynchronously.
 *
 * Polls /build-status and renders a staged checklist + progress bar + Beacon
 * shimmer, so the heavy data pull feels guided rather than a frozen spinner.
 */
import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, Circle, AlertTriangle, Radar } from 'lucide-react';

import { Button } from '../ui/button';
import { BeaconShimmer } from './BeaconShimmer';
import { getBuildStatus } from '../../api/budgets';

export function BudgetBuildProgress({ accountId, budgetId, name, onReady }) {
  const [status, setStatus] = useState(null);
  const timer = useRef(null);
  const mountedAt = useRef(Date.now());
  const MIN_VISIBLE_MS = 2200; // let users actually see the journey on tiny builds

  useEffect(() => {
    let active = true;
    const finish = () => {
      const elapsed = Date.now() - mountedAt.current;
      const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
      timer.current = setTimeout(() => { if (active) onReady?.(); }, wait);
    };
    const poll = async () => {
      try {
        const d = await getBuildStatus({ accountId, id: budgetId });
        if (!active) return;
        setStatus(d);
        if (d.status === 'ready') { finish(); return; }
      } catch { /* keep polling */ }
      timer.current = setTimeout(poll, 1000);
    };
    poll();
    return () => { active = false; if (timer.current) clearTimeout(timer.current); };
  }, [accountId, budgetId, onReady]);

  const steps = status?.steps || [
    { key: 'queued', label: 'Queued', state: 'active' },
  ];
  const doneCount = steps.filter((s) => s.state === 'done').length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const failed = status?.status === 'failed';

  return (
    <div className="max-w-xl mx-auto mt-10 border rounded-xl bg-card p-8 shadow-sm"
      data-testid="budget-build-progress">
      <div className="flex items-center gap-3 mb-1">
        <span className="w-9 h-9 rounded-lg bg-violet-600 flex items-center justify-center">
          <Radar className="w-5 h-5 text-white" />
        </span>
        <div>
          <h2 className="text-lg font-bold tracking-tight">Preparing your budget workspace</h2>
          <p className="text-sm text-muted-foreground">{name || 'New budget'}</p>
        </div>
      </div>

      {!failed && (
        <div className="my-5">
          <BeaconShimmer />
          <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-violet-600 transition-all duration-500 ease-out"
              style={{ width: `${pct}%` }} data-testid="build-progress-bar" />
          </div>
        </div>
      )}

      <ul className="space-y-2.5 mt-4">
        {steps.map((s) => (
          <li key={s.key} className="flex items-center gap-2.5 text-sm" data-testid={`build-step-${s.key}`}>
            {s.state === 'done' && <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
            {s.state === 'active' && <Loader2 className="w-4 h-4 text-violet-600 animate-spin shrink-0" />}
            {s.state === 'pending' && <Circle className="w-4 h-4 text-muted-foreground/40 shrink-0" />}
            {s.state === 'failed' && <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />}
            <span className={s.state === 'pending' ? 'text-muted-foreground' : 'font-medium'}>{s.label}</span>
          </li>
        ))}
      </ul>

      {failed && (
        <div className="mt-5 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm"
          data-testid="build-failed">
          <p className="font-medium text-destructive flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> Build failed
          </p>
          <p className="text-muted-foreground mt-1">{status?.error || 'Something went wrong while building the baseline.'}</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      )}

      {!failed && (
        <p className="text-xs text-muted-foreground mt-5">
          We're pulling your historical bills and modelling supply, delivery, seasonality &amp;
          rates. This usually takes a few seconds — you can keep this tab open.
        </p>
      )}
    </div>
  );
}
